import { inject, Injectable } from '@angular/core';
import { Route } from '@angular/router';
import { PermissionServiceBase } from '@ccfx/shared-lib';
import { OnboardingStatusService } from '../../onboarding/services/onboarding-status.service';

/**
 * Customer Portal concrete permission service.
 * Extends the shared-lib base class with customer portal-specific nav items and route permissions.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService extends PermissionServiceBase {
  private readonly onboardingStatus = inject(OnboardingStatusService);

  // Set to true to enable the Shipping Fees page in the customer portal
  private readonly showShippingFees = false;

  public override NavcomponentMap: Map<string, string> = new Map<string, string>([
    ['home', 'nav.home'],
    ['onboarding', 'nav.onboarding'],
    ['user-onboarding', 'nav.userOnboarding'],
    ['order-management', 'nav.orderManagement'],
    ...(this.showShippingFees
      ? ([['shipping-fees', 'nav.shippingFees']] as [string, string][])
      : []),
    ['user-management', 'nav.userManagement'],
  ]);

  override readonly assignedPermissions = new Set<string>([
    'home.view',
    'order.view',
    'user-management.view',
  ]);

  private readonly routePermissions: Record<string, string[]> = {
    home: ['home.view'],
    'order-management': ['order.view'],
    'shipping-fees': ['order.view'],
    'user-management': ['user-management.view'],
  };

  override hasPermission(permission: string): boolean {
    return this.assignedPermissions.has(permission);
  }

  override hasAccess(routePath: string): boolean {
    // Company onboarding is visible to:
    //   - corporate admins in PendingKYC state (reviewing/submitted application)
    //   - canSign non-admin users when company is still Pending/Invited (to fill company KYC first)
    if (routePath === 'onboarding') {
      const status = this.onboardingStatus.status();
      const isAdmin = this.onboardingStatus.isCorporateAdmin();
      const isPortalAccessible = this.onboardingStatus.isPortalAccessible();
      return (
        (status === 'PendingKYC' && isAdmin) ||
        (!isPortalAccessible && this.onboardingStatus.canSign() && !isAdmin)
      );
    }
    // User KYC is accessible to:
    // 1. Any user whose company is Active but personal account is not yet active.
    // 2. A corporate admin whose company is PendingKYC and who hasn't submitted personal KYC yet.
    if (routePath === 'user-onboarding') {
      const activeCompanyUserPending =
        this.onboardingStatus.status() === 'Active' && !this.onboardingStatus.isUserActive();
      const pendingKycAdminNeedsKyc =
        this.onboardingStatus.status() === 'PendingKYC' &&
        this.onboardingStatus.isCorporateAdmin() &&
        !this.onboardingStatus.isUserActive();
      return activeCompanyUserPending || pendingKycAdminNeedsKyc;
    }
    // Shipping fees page is feature-flagged
    if (routePath === 'shipping-fees') return this.showShippingFees;
    // User management is only accessible to company admins
    if (routePath === 'user-management') {
      return this.onboardingStatus.isCorporateAdmin();
    }
    const permissions = this.routePermissions[routePath];
    if (!permissions) return true;
    return permissions.some((p) => this.assignedPermissions.has(p));
  }

  override iconForRoute(route: string): string {
    if (route === 'onboarding') return 'fa-upload';
    if (route === 'user-onboarding') return 'fa-user-check';
    if (route === 'order-management') return 'fa-clipboard-list';
    if (route === 'user-management') return 'fa-users-cog';
    if (route === 'shipping-fees') return 'fa-shipping-fast';
    return super.iconForRoute(route);
  }

  override getAllowedRoutes(routes: Route[]): Route[] {
    return routes.filter((route) => route.path && this.hasAccess(route.path));
  }

  override hasAnyAccessTo(routes: Route[]): boolean {
    return this.getAllowedRoutes(routes).length > 0;
  }

  override defaultRoute: string = 'home';

  override firstAllowedPath(routes: Route[]): string | null {
    const allowed = this.getAllowedRoutes(routes);
    const preferred = allowed.find((r) => r.path === this.defaultRoute);
    return (preferred ?? allowed[0])?.path ?? null;
  }
}
